import * as React from "react";

export const SCROLL_STEP_RATIO = 0.8;

// Horizontal extension of the tab item's focus-visible ring (`-left-[3px]` /
// `-right-[3px]` in tabs-variants). Used when auto-scrolling so the ring on
// an edge-activated tab isn't clipped or hidden behind a scroll chevron.
const FOCUS_RING_OFFSET = 3;

// Matches `w-[34px]` on the scroll chevrons. When a chevron is visible we
// scroll the focused/active tab past it so its focus ring isn't covered.
const SCROLL_BUTTON_WIDTH = 34;

export function getScrollBehavior(): ScrollBehavior {
  if (typeof window === "undefined") return "smooth";
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";
}

export function useTabsOverflow(
  wrapperRef: React.RefObject<HTMLDivElement | null>,
  scrollerRef: React.RefObject<HTMLElement | null>
) {
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [hasOverflow, setHasOverflow] = React.useState(false);

  const update = React.useCallback(() => {
    const scroller = scrollerRef.current;
    const wrapper = wrapperRef.current;
    if (!scroller || !wrapper) return;
    // Measure overflow against the wrapper's available width — not the
    // scroller's — so that once buttons render and shrink the scroller, we
    // don't latch into a permanent `hasOverflow = true` state when the user
    // resizes back to a width where the items would fit without buttons.
    const overflow = scroller.scrollWidth - wrapper.clientWidth > 1;
    setHasOverflow(overflow);
    setCanScrollLeft(scroller.scrollLeft > 0);
    setCanScrollRight(
      overflow &&
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1
    );
  }, [scrollerRef, wrapperRef]);

  React.useEffect(() => {
    const scroller = scrollerRef.current;
    const wrapper = wrapperRef.current;
    if (!scroller || !wrapper) return;
    update();
    const observer = new ResizeObserver(update);
    observer.observe(scroller);
    observer.observe(wrapper);
    scroller.addEventListener("scroll", update, { passive: true });
    return () => {
      observer.disconnect();
      scroller.removeEventListener("scroll", update);
    };
  }, [scrollerRef, wrapperRef, update]);

  return { canScrollLeft, canScrollRight, hasOverflow };
}

// Scroll only the given scroller horizontally — not via scrollIntoView, which
// would also scroll any vertically-scrollable ancestor (including the page)
// and cause a visible vertical jump on activation.
export function scrollActiveTabIntoView(
  item: HTMLElement,
  scrollerSelector: string
) {
  const scroller = item.closest<HTMLElement>(scrollerSelector);
  if (!scroller) return;
  const itemLeft = item.offsetLeft;
  const itemRight = itemLeft + item.offsetWidth;
  const scrollLeft = scroller.scrollLeft;
  const clientWidth = scroller.clientWidth;
  const scrollRight = scrollLeft + clientWidth;
  // The chevrons are absolutely positioned at the scroller's edges. They sit
  // on top of items by design — so treat their width as a scroll-padding
  // inset and stop the item past them. When that side isn't scrollable the
  // chevron is `opacity-0` and shouldn't push the item around.
  const leftInset = scrollLeft > 0 ? SCROLL_BUTTON_WIDTH : 0;
  const rightInset =
    scrollLeft + clientWidth < scroller.scrollWidth - 1
      ? SCROLL_BUTTON_WIDTH
      : 0;
  const behavior = getScrollBehavior();
  if (itemLeft - FOCUS_RING_OFFSET < scrollLeft + leftInset) {
    scroller.scrollTo({
      left: itemLeft - FOCUS_RING_OFFSET - leftInset,
      behavior,
    });
  } else if (itemRight + FOCUS_RING_OFFSET > scrollRight - rightInset) {
    scroller.scrollTo({
      left: itemRight + FOCUS_RING_OFFSET + rightInset - clientWidth,
      behavior,
    });
  }
}
